import { Link } from "@tanstack/react-router";
import { Eye, HelpCircle, LayoutDashboard, LogOut, Settings } from "lucide-react";
import { useState } from "react";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useImpersonation } from "@/hooks/use-impersonation";
import { useMyPerson } from "@/hooks/use-persons";
import { useSignOut } from "@/hooks/use-sign-out";
import { useSession } from "@/lib/auth-client";
import { canEditAll, canViewAll } from "@/lib/permissions";
import { ViewAsDialog } from "./ViewAsDialog";

/**
 * Topbar account menu. The avatar used to be a dead, non-interactive circle;
 * account actions (Sign Out) lived only at the bottom of the sidebar. This
 * makes the avatar the account entry point every user reflexively clicks.
 * Shows the person's profile photo when one is set, initials otherwise.
 */
export function UserMenu() {
  const { data: session } = useSession();
  const { data: me } = useMyPerson();
  const signOut = useSignOut();
  const isAdmin = canViewAll(session?.user?.role);
  // "View as…" is the EDIT tier only (the server enforces the same), and is
  // hidden while already viewing as someone — the banner's Stop is the only
  // sensible action then, and the server refuses nesting anyway.
  const canViewAs = canEditAll(session?.user?.role);
  const { active: impersonating } = useImpersonation();
  const [viewAsOpen, setViewAsOpen] = useState(false);

  if (!session?.user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="rounded-full transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label="Account menu"
        >
          <PersonAvatar
            name={session.user.name ?? "User"}
            seed={me?.id ?? session.user.id}
            imageUrl={me?.avatarUrl}
            className="size-8 rounded-full text-xs"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="truncate font-medium">{session.user.name}</span>
            <span className="truncate text-xs font-normal text-muted-foreground">
              {session.user.email}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/dashboard/my">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              My Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/dashboard/help">
              <HelpCircle className="mr-2 h-4 w-4" />
              Help &amp; guide
            </Link>
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/dashboard/settings">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
          )}
          {canViewAs && !impersonating && (
            <DropdownMenuItem onSelect={() => setViewAsOpen(true)}>
              <Eye className="mr-2 h-4 w-4" />
              View as…
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {canViewAs && <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />}
    </>
  );
}
